import { useCallback, useState, type FormEvent } from 'react';
import type { EscalationChain } from '@detect/shared';
import { chainService, recipientGroupService } from '../../services/alerting';
import type { WithId } from '../../services/types';
import { useTenant } from '../../contexts/TenantContext';
import { useCursorList } from '../../hooks/useCursorList';
import { usePickerOptions } from '../../hooks/usePickerOptions';
import {
  LoadingSpinner,
  ErrorState,
  EmptyState,
  Modal,
  LoadMoreButton,
} from '../../components/common';
import { CHANGE_NOT_RECOVERED_DEFAULT } from './ruleKinds';
import { RecipientsEditor } from './RecipientsEditor';
import {
  draftToRecipients,
  recipientsToDraft,
  type RecipientsDraft,
} from './recipients-draft';

interface StageDraft {
  recipients: RecipientsDraft;
  recipientGroupIds: string[];
  escalationMinutes: string;
  activeHours: 'always' | 'business' | 'after_hours';
}

const EMPTY_STAGE: StageDraft = {
  recipients: { emails: '', integrationIds: [] },
  recipientGroupIds: [],
  escalationMinutes: '15',
  activeHours: 'always',
};

export function ChainsTab() {
  const { tenantId, hasRole } = useTenant();
  const [editing, setEditing] = useState<WithId<EscalationChain> | 'new' | null>(null);
  const [actionError, setActionError] = useState<unknown>(null);

  const fetchPage = useCallback(
    (tid: string, startAfter: string | undefined) =>
      chainService.list(tid, { limit: 50, startAfter }),
    []
  );
  const list = useCursorList<WithId<EscalationChain>>(fetchPage, 'all');

  const remove = async (chain: WithId<EscalationChain>) => {
    if (!tenantId) return;
    if (!window.confirm(`Delete escalation chain ${chain.name}?`)) return;
    setActionError(null);
    try {
      await chainService.remove(tenantId, chain.id);
      void list.reload();
    } catch (err) {
      setActionError(err);
    }
  };

  return (
    <div>
      <div className="page-actions tab-actions">
        {hasRole('admin') && (
          <button
            type="button"
            className="btn btn-primary"
            onClick={() => setEditing('new')}
          >
            New chain
          </button>
        )}
      </div>
      {actionError !== null && <ErrorState error={actionError} />}
      <p className="text-muted">
        {CHANGE_NOT_RECOVERED_DEFAULT} Chains are chosen by alert rules (Alerting, Rules).
      </p>
      {list.error ? (
        <ErrorState error={list.error} onRetry={() => void list.reload()} />
      ) : list.loading ? (
        <LoadingSpinner />
      ) : list.items.length === 0 ? (
        <EmptyState
          title="No escalation chains"
          description="Chains define who hears about an alert, in what order."
        />
      ) : (
        <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Stages</th>
                <th>Repeat</th>
                <th>Enabled</th>
                {hasRole('admin') && <th aria-label="Actions" />}
              </tr>
            </thead>
            <tbody>
              {list.items.map((c) => (
                <tr key={c.id}>
                  <td>{c.name}</td>
                  <td>{c.stages.length}</td>
                  <td>{c.repeatUntilAck ? `until ack (max ${c.maxRepeatCycles})` : 'no'}</td>
                  <td>{c.enabled ? 'yes' : 'no'}</td>
                  {hasRole('admin') && (
                    <td className="row-actions">
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => setEditing(c)}
                      >
                        Edit
                      </button>
                      <button
                        type="button"
                        className="btn btn-ghost"
                        onClick={() => void remove(c)}
                      >
                        Delete
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <LoadMoreButton
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        loadMoreError={list.loadMoreError}
        onLoadMore={() => void list.loadMore()}
      />
      {editing !== null && (
        <ChainModal
          chain={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            void list.reload();
          }}
        />
      )}
    </div>
  );
}

function ChainModal({
  chain,
  onClose,
  onSaved,
}: {
  chain: WithId<EscalationChain> | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const { tenantId } = useTenant();
  const [name, setName] = useState(chain?.name ?? '');
  const [enabled, setEnabled] = useState(chain?.enabled ?? true);
  const [repeatUntilAck, setRepeatUntilAck] = useState(chain?.repeatUntilAck ?? false);
  const [maxRepeatCycles, setMaxRepeatCycles] = useState(
    String(chain?.maxRepeatCycles ?? 5)
  );
  const [stages, setStages] = useState<StageDraft[]>(
    chain
      ? chain.stages.map((s) => ({
          recipients: recipientsToDraft(s.recipients),
          recipientGroupIds: s.recipientGroupIds,
          escalationMinutes: String(s.escalationMinutes),
          activeHours: s.activeHours,
        }))
      : [{ ...EMPTY_STAGE }]
  );
  // Business-hours window (#6) — required by the API whenever any stage
  // gates on business/after hours.
  const [bhDays, setBhDays] = useState<ReadonlySet<number>>(
    new Set(chain?.businessHours?.daysOfWeek ?? [1, 2, 3, 4, 5])
  );
  const [bhStart, setBhStart] = useState(chain?.businessHours?.startTime ?? '08:00');
  const [bhEnd, setBhEnd] = useState(chain?.businessHours?.endTime ?? '18:00');
  const [error, setError] = useState<unknown>(null);
  const [submitting, setSubmitting] = useState(false);

  const groupsQuery = usePickerOptions(['recipient-groups'], (tid, startAfter) =>
    recipientGroupService.list(tid, { limit: 100, startAfter })
  );
  const groups = groupsQuery.data?.items ?? [];

  const setStage = (index: number, patch: Partial<StageDraft>) => {
    setStages((prev) => prev.map((s, i) => (i === index ? { ...s, ...patch } : s)));
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId) return;
    setSubmitting(true);
    setError(null);
    try {
      const gated = stages.some((s) => s.activeHours !== 'always');
      if (gated && bhDays.size === 0) {
        setError(new Error('Pick at least one business-hours day.'));
        setSubmitting(false);
        return;
      }
      const payload = {
        name,
        enabled,
        repeatUntilAck,
        maxRepeatCycles: Number(maxRepeatCycles),
        stages: stages.map((s) => ({
          recipients: draftToRecipients(s.recipients),
          recipientGroupIds: s.recipientGroupIds,
          escalationMinutes: Number(s.escalationMinutes),
          activeHours: s.activeHours,
        })),
        ...(gated
          ? {
              businessHours: {
                daysOfWeek: [...bhDays].sort((a, b) => a - b),
                startTime: bhStart,
                endTime: bhEnd,
                timezone:
                  Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
              },
            }
          : {}),
      };
      if (chain) {
        // Un-gating an existing chain must CLEAR the stored window —
        // omitting the field would leave stale config to resurface on
        // re-gating (codex finding 2). Only updates accept null.
        await chainService.update(tenantId, chain.id, {
          ...payload,
          ...(!gated && chain.businessHours ? { businessHours: null } : {}),
        });
      } else {
        await chainService.create(tenantId, payload);
      }
      onSaved();
    } catch (err) {
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={chain ? `Edit ${chain.name}` : 'New escalation chain'}
      onClose={onClose}
      wide
    >
      <form onSubmit={submit}>
        {error !== null && <ErrorState error={error} />}
        <div className="form-field">
          <label htmlFor="chain-name">Name</label>
          <input
            id="chain-name"
            name="name"
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <div className="form-field form-field-inline">
          <input
            id="chain-enabled"
            name="enabled"
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          <label htmlFor="chain-enabled">Enabled</label>
        </div>
        <div className="form-field form-field-inline">
          <input
            id="chain-repeat"
            name="repeatUntilAck"
            type="checkbox"
            checked={repeatUntilAck}
            onChange={(e) => setRepeatUntilAck(e.target.checked)}
          />
          <label htmlFor="chain-repeat">Repeat from stage 1 until acknowledged</label>
        </div>
        {repeatUntilAck && (
          <div className="form-field">
            <label htmlFor="chain-cycles">Max repeat cycles</label>
            <input
              id="chain-cycles"
              name="maxRepeatCycles"
              type="number"
              min={1}
              max={20}
              value={maxRepeatCycles}
              onChange={(e) => setMaxRepeatCycles(e.target.value)}
            />
          </div>
        )}

        <h3>Stages</h3>
        {stages.map((stage, index) => (
          <fieldset key={index} className="stage-editor">
            <legend>
              Stage {index + 1}
              {stages.length > 1 && (
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() =>
                    setStages((prev) => prev.filter((_, i) => i !== index))
                  }
                >
                  Remove
                </button>
              )}
            </legend>
            <RecipientsEditor
              draft={stage.recipients}
              onChange={(next) => setStage(index, { recipients: next })}
            />
            <div className="form-field">
              <label htmlFor={`stage-${index}-groups`}>Recipient groups</label>
              <select
                id={`stage-${index}-groups`}
                name={`stage-${index}-groups`}
                multiple
                size={3}
                value={stage.recipientGroupIds}
                onChange={(e) =>
                  setStage(index, {
                    recipientGroupIds: Array.from(e.target.selectedOptions).map(
                      (o) => o.value
                    ),
                  })
                }
              >
                {groups.map((g) => (
                  <option key={g.id} value={g.id}>
                    {g.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="form-field">
              <label htmlFor={`stage-${index}-minutes`}>
                Wait before next stage (minutes)
              </label>
              <input
                id={`stage-${index}-minutes`}
                name={`stage-${index}-minutes`}
                type="number"
                min={1}
                max={1440}
                required
                value={stage.escalationMinutes}
                onChange={(e) => setStage(index, { escalationMinutes: e.target.value })}
              />
            </div>
            <div className="form-field">
              <label htmlFor={`stage-${index}-hours`}>Fires</label>
              <select
                id={`stage-${index}-hours`}
                name={`stage-${index}-hours`}
                value={stage.activeHours}
                onChange={(e) =>
                  setStage(index, {
                    activeHours: e.target.value as StageDraft['activeHours'],
                  })
                }
              >
                <option value="always">Any time</option>
                <option value="business">Business hours only</option>
                <option value="after_hours">After hours only</option>
              </select>
            </div>
          </fieldset>
        ))}
        {stages.some((s) => s.activeHours !== 'always') && (
          <fieldset className="stage-editor">
            <legend>Business hours</legend>
            <p className="field-hint">
              Gated stages fire inside (or outside) this window; times are
              in your browser timezone.
            </p>
            <div className="form-field">
              <span className="visually-hidden" id="chain-bh-days-label">
                Business days
              </span>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(
                (label, day) => (
                  <label key={day} className="checkbox-label checkbox-inline">
                    <input
                      type="checkbox"
                      checked={bhDays.has(day)}
                      onChange={(e) =>
                        setBhDays((prev) => {
                          const next = new Set(prev);
                          if (e.target.checked) next.add(day);
                          else next.delete(day);
                          return next;
                        })
                      }
                    />
                    {label}
                  </label>
                )
              )}
            </div>
            <div className="form-field">
              <label htmlFor="chain-bh-start">From</label>
              <input
                id="chain-bh-start"
                name="bhStart"
                type="time"
                required
                value={bhStart}
                onChange={(e) => setBhStart(e.target.value)}
              />
            </div>
            <div className="form-field">
              <label htmlFor="chain-bh-end">Until</label>
              <input
                id="chain-bh-end"
                name="bhEnd"
                type="time"
                required
                value={bhEnd}
                onChange={(e) => setBhEnd(e.target.value)}
              />
            </div>
          </fieldset>
        )}
        {stages.length < 10 && (
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => setStages((prev) => [...prev, { ...EMPTY_STAGE }])}
          >
            Add stage
          </button>
        )}
        <div className="modal-footer">
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Saving…' : chain ? 'Save' : 'Create'}
          </button>
        </div>
      </form>
    </Modal>
  );
}
